/**
 * Mobile Layout Component
 *
 * Layout wrapper for mobile application pages using per-page headers at top and
 * NavigationMobile (tabs + checkout button + order status toast) at bottom.
 *
 * All orchestration logic lives in useLayoutOrchestration — this component is a pure
 * rendering shell. The same hook is consumed by DesktopLayout.
 *
 * Shared overlays (sheets + alerts) are rendered by LayoutOverlays.
 */

import { ReactNode, useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  NavigationMobile,
  HeaderMenu,
  HeaderCart,
  HeaderOther,
  HeaderRewards,
  Icon,
  Toast,
  useRewardsStore,
  useUIStore,
} from '@restaurant/core'
import { useLayoutOrchestration } from '../../hooks/useLayoutOrchestration'
import { LayoutOverlays } from './LayoutOverlays'
import { DebugBadge } from './DebugBadge'

// ---------------------------------------------------------------------------
// Route -> Header mapping
// ---------------------------------------------------------------------------

const headerMap: Record<string, 'menu' | 'rewards' | 'cart' | 'other'> = {
  '/': 'menu',
  '/menu': 'menu',
  '/rewards': 'rewards',
  '/rewards/history': 'other',
  '/cart': 'cart',
}

const pageNameMap: Record<string, string> = {
  '/checkout': 'Checkout',
  '/orders': 'Orders',
  '/account': 'Account',
  '/account/manage': 'Manage Account',
  '/account/notifications': 'Notifications',
  '/account/addresses': 'Addresses',
  '/favorites': 'Favorites',
  '/reorder': 'Reorder',
  '/help': 'Help',
  '/offers': 'Offers',
  '/rewards/history': 'Points History',
}

function getPageName(pathname: string): string {
  if (pageNameMap[pathname]) return pageNameMap[pathname]
  if (pathname.startsWith('/reorder/')) return 'Reorder'
  if (pathname.startsWith('/orders/') && pathname.endsWith('/details')) return 'Order Details'
  if (pathname.startsWith('/orders/')) return 'Order'
  return ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface MobileLayoutProps {
  children: ReactNode
}

export function MobileLayout({ children }: MobileLayoutProps) {
  const o = useLayoutOrchestration()
  const navigate = useNavigate()
  const { balance, merchantConfig } = useRewardsStore()
  const { toastVisible, toastMessage, dismissToast } = useUIStore()

  // Compute points until next reward level
  let pointsUntilNextReward: number | undefined
  if (merchantConfig) {
    if (merchantConfig.redemption_type === 'item' && merchantConfig.item_redemption_groups) {
      // Item-based: find the lowest group threshold the user hasn't reached
      const nextThreshold = merchantConfig.item_redemption_groups
        .map(g => g.point_value)
        .filter(v => v > balance)
        .sort((a, b) => a - b)[0]
      if (nextThreshold != null) pointsUntilNextReward = nextThreshold - balance
    } else if (merchantConfig.redemption_type === 'dollar' && merchantConfig.point_value) {
      // Dollar-based: points needed to reach the next redemption increment
      const remainder = balance % merchantConfig.point_value
      pointsUntilNextReward = remainder === 0 ? 0 : merchantConfig.point_value - remainder
    }
  }

  const headerType = headerMap[o.location.pathname] ?? 'other'
  const debugBadge = o.isDebugMode ? <DebugBadge /> : undefined
  const isMenuPage = headerType === 'menu'

  // ---- Scroll-linked header for menu page ----
  const headerRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)
  const bannerHeightRef = useRef(0)
  const [headerHeight, setHeaderHeight] = useState(0)

  // Measure header and banner heights. ResizeObserver re-measures when the
  // banner image loads and changes the header's intrinsic height.
  useEffect(() => {
    const header = headerRef.current
    if (!header || !isMenuPage) return

    const measure = () => {
      const pinnedContent = header.querySelector('[data-header-pinned-content]')
      const fullHeight = header.offsetHeight
      const pinnedHeight = pinnedContent ? (pinnedContent as HTMLElement).offsetHeight : fullHeight
      bannerHeightRef.current = fullHeight - pinnedHeight
      setHeaderHeight(fullHeight)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(header)
    measure()

    return () => observer.disconnect()
  }, [isMenuPage])

  // Reset scroll position and header transform on route change.
  useEffect(() => {
    const main = mainRef.current
    if (main) main.scrollTop = 0
    const header = headerRef.current
    if (header) header.style.transform = ''
  }, [o.location.pathname])

  // Drive header translateY from the menu page's scroll position.
  const handleMenuScroll = useCallback(() => {
    const main = mainRef.current
    const header = headerRef.current
    if (!main || !header) return
    o.handleScrollSpy(main)
    const bh = bannerHeightRef.current
    if (bh === 0) return
    const translateY = -Math.min(main.scrollTop, bh)
    header.style.transform = `translateY(${translateY}px)`
  }, [o.handleScrollSpy])

  const noBackRoutes = ['/account', '/offers']
  const showBack = !noBackRoutes.includes(o.location.pathname)

  return (
    <div className="h-[100dvh] flex flex-col relative" style={{ backgroundColor: 'var(--theme-fill-page-background)' }}>
      {/* ---- Header ---- */}
      {isMenuPage ? (
        // Menu page: header is absolutely positioned over content and
        // translated up as the user scrolls, driven by handleMenuScroll.
        <div
          ref={headerRef}
          className="absolute top-0 left-0 right-0 z-10"
          style={{ touchAction: 'none' }}
        >
          <HeaderMenu
            locationName={o.currentLocation.name}
            addressLine1={o.currentLocation.addressLine1}
            addressLine2={o.currentLocation.addressLine2}
            multipleLocations={o.hasMultipleLocations}
            selectedOrderType={o.orderType}
            showBanner={!!o.currentRestaurant?.bannerUrl}
            bannerUrl={o.currentRestaurant?.bannerUrl}
            locationBadge={debugBadge}
            onOrderTypeChange={o.handleOrderTypeChange}
            onChangeLocationPress={o.handleChangeLocationPress}
            toolbar={o.toolbar}
          />
        </div>
      ) : (
        // Other pages: header is fixed in the flex flow.
        <div className="shrink-0" style={{ touchAction: 'none', transform: 'none' }}>
          {headerType === 'rewards' && (
            <HeaderRewards
              points={balance}
              pointsUntilNextLevel={pointsUntilNextReward}
              onHistoryPress={o.isAuthenticated ? () => navigate('/rewards/history') : undefined}
              testID="mobile-header-rewards"
            />
          )}
          {headerType === 'cart' && (
            <HeaderCart
              locationName={o.currentLocation.name}
              addressLine1={o.currentLocation.addressLine1}
              addressLine2={o.currentLocation.addressLine2}
              multipleLocations={o.hasMultipleLocations}
              selectedOrderType={o.orderType}
              locationBadge={debugBadge}
              onOrderTypeChange={o.handleOrderTypeChange}
              onChangeLocationPress={o.handleChangeLocationPress}
              testID="mobile-header-cart"
            />
          )}
          {headerType === 'other' && (
            <HeaderOther
              pageName={getPageName(o.location.pathname)}
              showBackButton={showBack}
              onBackPress={showBack ? () => navigate(-1) : undefined}
              testID="mobile-header-other"
            />
          )}
        </div>
      )}

      {/* ---- Scrollable content ---- */}
      <main
        ref={mainRef}
        className="flex-1 flex flex-col overflow-y-auto"
        style={{
          overscrollBehavior: 'none',
          ...(isMenuPage && headerHeight > 0 && {
            paddingTop: headerHeight,
            '--pinned-header-height': `${headerHeight - bannerHeightRef.current}px`,
          } as React.CSSProperties),
        }}
        onScroll={isMenuPage ? handleMenuScroll : undefined}
      >
        {children}
      </main>

      {/* Action toast notification */}
      {toastVisible && toastMessage && (
        <div className="shrink-0 flex justify-center" style={{ padding: '0 var(--layout-padding-page-horizontal) var(--layout-space-02)' }}>
          <Toast
            title={toastMessage}
            onPress={dismissToast}
            testID="action-toast"
          />
        </div>
      )}

      {/* ---- Navigation — always fixed at bottom ---- */}
      <div className="shrink-0" style={{ touchAction: 'none' }}>
        <NavigationMobile
          selectedTabId={o.selectedTabId}
          onTabChange={o.handleTabChange}
          showOffers={o.showOffers}
          showRewards={o.showRewards}
          checkoutButtonState={o.location.pathname === '/cart' ? 'Hidden' : o.checkoutButtonState}
          orderTotal={o.checkoutTotal}
          onCheckoutPress={o.handleCheckoutClick}
          showToastOrderStatus={o.orderStatusToast.visible}
          orderStatus={{
            title: o.orderStatusToast.title,
            description: o.orderStatusToast.description,
            icon: o.orderStatusToast.iconName ? (
              <Icon name={o.orderStatusToast.iconName} size="lg" className="text-theme-icon" />
            ) : undefined,
          }}
          onOrderStatusPress={o.orderStatusToast.onToastClick}
          testID="mobile-navigation"
        />
      </div>

      {/* Shared overlays: sheets + alerts */}
      <LayoutOverlays orchestration={o} />
    </div>
  )
}
